import React from 'react';
import { Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import DataTable from '../common/DataTable';
import AccountForm from './AccountForm';
import AccountTypeBadge from '../common/AccountTypeBadge';
import { formatCurrency } from '../../utils/formatters';

const AccountListSection = ({ 
  title, 
  icon, 
  variant, 
  type, 
  isEditor, 
  accounts, 
  onSubmit, 
  onEdit, 
  onToggleActive,
  headers,
  extraAction,
  sortBy,
  sortOrder,
  onSort
}) => {
  const bsTypes = [
    'Bank', 'Accounts receivable (A/R)', 'Other Current Assets', 'Fixed Assets', 'Other Assets',
    'Credit Card', 'Accounts payable (A/P)', 'Other Current Liabilities', 'Long Term Liabilities', 'Equity'
  ];

  const formatAccountName = (acc) => {
    return acc.accountNumber ? `${acc.accountNumber}-${acc.name}` : acc.name;
  };

  return (
    <div className={`mb-5 p-4 bg-white rounded shadow-sm border-left border-${variant}`} style={{ borderLeftWidth: '5px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        {(title || icon) && (
          <h3 className={`mb-0 text-${variant} font-weight-bold`}>
            {icon && <i className={`${icon} mr-2`}></i>} {title}
          </h3>
        )}
        {extraAction}
      </div>
      <Row>
        <Col md={12}>
          <DataTable 
            headers={headers}
            data={accounts}
            emptyMessage={`No ${title ? title.toLowerCase() : 'accounts'} yet.`}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
            renderRow={(acc) => {
              const isBS = bsTypes.includes(acc.type);
              const displayBalance = isBS ? acc.balance : Math.abs(acc.balance);
              
              // Determine depth for sub-account visual indentation
              let depth = 0;
              let curr = acc;
              while (curr.parentId) {
                const parent = accounts.find(a => {
                  const currParentIdStr = typeof curr.parentId === 'object' ? (curr.parentId._id ? curr.parentId._id.toString() : '') : curr.parentId.toString();
                  return a._id.toString() === currParentIdStr;
                });
                if (!parent) break;
                depth++;
                curr = parent;
              }
              const indentStyle = { paddingLeft: `${depth * 25}px` };

              return (
                <tr key={acc._id} style={acc.isActive === false ? { opacity: 0.6, backgroundColor: '#f9f9f9' } : {}}>
                  <td className="align-middle font-weight-bold" style={indentStyle}>
                    <Link to={`/accounts/${acc._id}/transactions`} className={`${acc.isActive === false ? 'text-secondary' : 'text-primary'} font-weight-bold hover-underline`}>
                      {formatAccountName(acc)}
                    </Link>
                    {acc.isActive === false && (
                      <Badge variant="secondary" className="ml-2 px-1 py-0 small" style={{ fontSize: '0.75rem' }}>Inactive</Badge>
                    )}
                    {acc.plaidConnectionId && (
                      <i 
                        className="fas fa-link ml-2 text-info" 
                        title="Connected to Bank via Plaid"
                        style={{ fontSize: '0.8rem' }}
                      ></i>
                    )}
                  </td>
                  <td className="align-middle">
                    <AccountTypeBadge type={acc.type} />
                  </td>
                  <td className={`text-right font-weight-bold align-middle ${displayBalance < 0 ? 'text-danger' : `text-${variant}`}`}>
                    {formatCurrency(displayBalance)}
                  </td>
                  <td className="text-right font-weight-bold align-middle text-muted">
                    {acc.bankBalance !== null && acc.bankBalance !== undefined ? (
                      <span className={acc.bankBalance < 0 ? 'text-danger' : ''}>
                        {formatCurrency(acc.bankBalance)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  {isEditor && (
                    <td className="text-right align-middle">
                      <button onClick={() => onEdit(acc)} className="btn btn-link btn-sm text-primary p-0 mr-3" title="Edit Account">
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        onClick={() => onToggleActive && onToggleActive(acc)} 
                        className={`btn btn-link btn-sm p-0 ${acc.isActive === false ? 'text-success' : 'text-danger'}`}
                        title={acc.isActive === false ? "Make Active" : "Make Inactive"}
                      >
                        <i className={acc.isActive === false ? "fas fa-check-circle" : "fas fa-ban"}></i>
                      </button>
                    </td>
                  )}
                </tr>
              );
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default AccountListSection;
